import { useCallback, useEffect, useState } from "react";

import { feetInchesSchema, type PresetScale } from "@/lib/geometry";
import type { ScaleToolsetRailController } from "@/viewport-tools/rail/types";
import type { CalibrationState, MeasurementScale } from "@/hooks/useMeasurementScale";
import { VIEWPORT_TOOLSET_IDS } from "@/viewport-tools/core/contracts";
import {
  CALIBRATION_METHOD_IDS,
  type CalibrationMethodId,
} from "@/viewport-tools/scale/registry";

/**
 * Controlled scale-toolset rail contract.
 *
 * Callers own the toolset expansion state via `expanded` and `onExpandedChange`.
 * Changing `expanded` to `false` triggers submenu teardown inside this hook,
 * including calibration cleanup through `cancelCalibration` when manual mode is
 * active. Callers are responsible for providing the current `calibrationState`
 * plus the calibration lifecycle callbacks (`startCalibration`,
 * `cancelCalibration`) so the hook can keep the rail UI and calibration session
 * synchronized.
 */
export interface UseScaleToolsetRailControllerArgs {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  calibrationState: CalibrationState | null;
  currentScale?: MeasurementScale | null;
  customDistanceInput: string;
  setCustomDistanceInput: (value: string) => void;
  startCalibration: () => void;
  applyCustomDistance: () => Promise<void>;
  applyPresetScale: (
    pageWidthInches: number,
    preset: PresetScale,
    pageAspectRatio?: number
  ) => Promise<void>;
  cancelCalibration: () => void;
  pagePixelWidth: number;
  pagePixelHeight: number;
}

export function useScaleToolsetRailController({
  expanded,
  onExpandedChange,
  calibrationState,
  currentScale,
  customDistanceInput,
  setCustomDistanceInput,
  startCalibration,
  applyCustomDistance,
  applyPresetScale,
  cancelCalibration,
  pagePixelWidth,
  pagePixelHeight,
}: UseScaleToolsetRailControllerArgs): ScaleToolsetRailController {
  const effectiveCalibrationState: CalibrationState =
    calibrationState ?? { phase: "idle" };
  const [validationError, setValidationError] = useState<string | null>(null);
  const [calibrationMethod, setCalibrationMethod] =
    useState<CalibrationMethodId | null>(null);

  const isCalibrating =
    effectiveCalibrationState.phase === "first_point_set" ||
    effectiveCalibrationState.phase === "second_point_set";
  const isPresetMode = calibrationMethod === CALIBRATION_METHOD_IDS.preset;
  const isManualMode = calibrationMethod === CALIBRATION_METHOD_IDS.manual;
  const isSubMenuOpen = isPresetMode || isManualMode;

  const closeCalibrationSubmenu = useCallback(() => {
    if (isManualMode && isCalibrating) {
      cancelCalibration();
    }
    setCalibrationMethod(null);
  }, [cancelCalibration, isCalibrating, isManualMode]);

  const setExpanded = (nextExpanded: boolean) => {
    if (!nextExpanded) {
      closeCalibrationSubmenu();
    }
    onExpandedChange(nextExpanded);
  };

  useEffect(() => {
    if (expanded || calibrationMethod === null) {
      return;
    }

    closeCalibrationSubmenu();
  }, [calibrationMethod, closeCalibrationSubmenu, expanded]);

  const applyScaleFromDistanceInput = async () => {
    const result = feetInchesSchema.safeParse(customDistanceInput);
    if (!result.success) {
      setValidationError(result.error.issues[0]?.message ?? "Invalid format");
      return;
    }

    setValidationError(null);
    try {
      await applyCustomDistance();
    } catch (error) {
      setValidationError(
        error instanceof Error ? error.message : "Failed to apply scale"
      );
      return;
    } finally {
      setCalibrationMethod(null);
    }
  };

  const applyScalePreset = async (preset: PresetScale, pageWidthInches: number) => {
    const pageAspectRatio =
      pagePixelWidth > 0 && pagePixelHeight > 0
        ? pagePixelHeight / pagePixelWidth
        : undefined;
    try {
      await applyPresetScale(pageWidthInches, preset, pageAspectRatio);
    } catch (error) {
      setValidationError(
        error instanceof Error ? error.message : "Failed to apply scale"
      );
    } finally {
      setCalibrationMethod(null);
    }
  };

  const cancelScaleCalibration = () => {
    cancelCalibration();
    setCalibrationMethod(null);
  };

  const selectScaleCalibrationMethod = (method: CalibrationMethodId) => {
    if (method !== CALIBRATION_METHOD_IDS.manual && isManualMode && isCalibrating) {
      cancelCalibration();
    }
    onExpandedChange(true);
    setCalibrationMethod(method);
    if (method === CALIBRATION_METHOD_IDS.manual) {
      startCalibration();
    }
  };

  const openScalePresetMode = () => {
    selectScaleCalibrationMethod(CALIBRATION_METHOD_IDS.preset);
  };

  return {
    toolsetId: VIEWPORT_TOOLSET_IDS.scale,
    expanded,
    setExpanded,
    calibrationState: effectiveCalibrationState,
    currentScale,
    customDistanceInput,
    setCustomDistanceInput,
    pagePixelWidth,
    pagePixelHeight,
    validationError,
    isCalibrating,
    isPresetMode,
    isManualMode,
    isSubMenuOpen,
    setValidationError,
    openScalePresetMode,
    selectScaleCalibrationMethod,
    applyScaleFromDistanceInput,
    applyScalePreset,
    cancelScaleCalibration,
  };
}
